import { useCallback, useEffect, useState } from 'react';
import {
  fetchFormationContent,
  fetchMyFormations,
  youtubeEmbedUrl,
  type FormationFile,
  type FormationLesson,
  type PublicFormation,
} from '@/lib/publicFormations';
import type { ZifekUser } from '@/hooks/useAuth';

interface Props {
  user: ZifekUser;
}

interface EnrolledCourse {
  formation: PublicFormation;
  grantedAt: string | null;
}

function fmtDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * « Mes formations » — les cours auxquels le client a accès (gratuits ou
 * payés). Le contenu (leçons, supports) s'ouvre dans une fenêtre intégrée.
 */
export default function MyCoursesTab({ user }: Props) {
  const [courses, setCourses] = useState<EnrolledCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<PublicFormation | null>(null);
  const [lessons, setLessons] = useState<FormationLesson[]>([]);
  const [files, setFiles] = useState<FormationFile[]>([]);
  const [contentLoading, setContentLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const rows = await fetchMyFormations(user.id);
      setCourses(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger vos formations.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { void load(); }, [load]);

  const openCourse = async (formation: PublicFormation) => {
    setOpen(formation);
    setContentLoading(true);
    setLessons([]);
    setFiles([]);
    try {
      const content = await fetchFormationContent(formation.id);
      setLessons(content.lessons);
      setFiles(content.files);
    } catch {
      /* silencieux : on affiche ce qui est disponible */
    } finally {
      setContentLoading(false);
    }
  };

  const embed = open ? youtubeEmbedUrl(open.video_url) : null;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Mes formations</h1>
          <p className="text-sm text-foreground-500 mt-1">
            Les cours auxquels vous avez accès, gratuits ou payés.
          </p>
        </div>
        <span className="px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold w-fit">
          {courses.length} formation{courses.length > 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-background-50 border border-background-200/50 rounded-lg">
          <i className="ri-error-warning-line text-3xl text-red-400 mb-3"></i>
          <p className="text-sm text-foreground-600 mb-4">{error}</p>
          <button
            onClick={() => void load()}
            className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            Réessayer
          </button>
        </div>
      ) : courses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-background-50 border border-background-200/50 rounded-lg">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-graduation-cap-line text-xl text-foreground-400"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-800 mb-1">Aucune formation pour le moment</h3>
          <p className="text-xs text-foreground-500 max-w-sm">
            Inscrivez-vous à une formation depuis le site d&apos;un formateur : elle apparaîtra
            automatiquement ici.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {courses.map(({ formation, grantedAt }) => (
            <button
              key={formation.id}
              type="button"
              onClick={() => void openCourse(formation)}
              className="text-left bg-background-50 border border-background-200/50 rounded-lg overflow-hidden hover:border-foreground-300/60 transition-colors cursor-pointer"
            >
              <div className="h-32 bg-background-100 overflow-hidden">
                {formation.image ? (
                  <img src={formation.image} alt={formation.titre} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-play-circle-line text-3xl text-foreground-300"></i>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2">{formation.titre}</h3>
                <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px] text-foreground-500">
                  <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 font-semibold">Accès actif</span>
                  {formation.duree && <span><i className="ri-time-line mr-1"></i>{formation.duree}</span>}
                </div>
                <p className="text-[11px] text-foreground-400 mt-2">Inscrit le {fmtDate(grantedAt)}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600">
                  Ouvrir le cours <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(null)}></div>
          <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/70">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70 sticky top-0 bg-background-50 z-10">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">{open.titre}</h3>
                <p className="text-[11px] text-foreground-500 truncate">
                  {open.categorie || 'Formation'} {open.duree ? `· ${open.duree}` : ''}
                </p>
              </div>
              <button
                onClick={() => setOpen(null)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {contentLoading ? (
                <div className="flex items-center justify-center py-10">
                  <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
                </div>
              ) : (
                <>
                  {embed ? (
                    <div className="aspect-video rounded-lg overflow-hidden bg-black">
                      <iframe
                        src={embed}
                        title={open.titre}
                        className="w-full h-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : open.video_url ? (
                    <a
                      href={open.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background-100 text-sm text-foreground-700 no-underline hover:bg-background-200/70 transition-colors"
                    >
                      <i className="ri-video-line text-primary-500"></i>Ouvrir la vidéo du cours
                    </a>
                  ) : null}

                  <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                    {open.description_long || open.description || 'Aucune description.'}
                  </p>

                  <div>
                    <h4 className="text-sm font-bold text-foreground-950 mb-2 flex items-center gap-2">
                      <i className="ri-list-check-2 text-primary-500"></i>Leçons
                    </h4>
                    {lessons.length === 0 ? (
                      <p className="text-xs text-foreground-500">Le programme détaillé n&apos;est pas encore disponible.</p>
                    ) : (
                      <div className="divide-y divide-background-200/60 border border-background-200/60 rounded-lg overflow-hidden">
                        {lessons.map((lesson, index) => (
                          <div key={lesson.id} className="flex items-center gap-3 px-4 py-3">
                            <span className="w-7 h-7 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                              {index + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-foreground-900 truncate">{lesson.titre || `Leçon ${index + 1}`}</p>
                              {lesson.duree && <p className="text-[11px] text-foreground-400">{lesson.duree}</p>}
                            </div>
                            {lesson.video_url && (
                              <a
                                href={lesson.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-semibold text-primary-600 no-underline whitespace-nowrap"
                              >
                                Voir
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {(files.length > 0 || open.pdf_url) && (
                    <div>
                      <h4 className="text-sm font-bold text-foreground-950 mb-2 flex items-center gap-2">
                        <i className="ri-attachment-2 text-primary-500"></i>Supports
                      </h4>
                      <div className="space-y-2">
                        {open.pdf_url && (
                          <a
                            href={open.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors no-underline"
                          >
                            <i className="ri-file-pdf-line text-red-500"></i>
                            <span className="text-sm text-foreground-800 flex-1 truncate">Support PDF</span>
                            <i className="ri-download-2-line text-foreground-500"></i>
                          </a>
                        )}
                        {files.map((file) => (
                          <a
                            key={file.id}
                            href={file.fichier || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors no-underline"
                          >
                            <i className="ri-file-line text-primary-500"></i>
                            <span className="text-sm text-foreground-800 flex-1 truncate">{file.titre || 'Ressource'}</span>
                            <i className="ri-download-2-line text-foreground-500"></i>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}